import React, { useEffect, useRef } from 'react';

import type { GoogleMapInstance } from '../../../../components/GooglePlaces/hooks/useGooglePlaces';

interface BuyerLocationMapProps {
  latitude: number;
  longitude: number;
  /** Marker tooltip — the buyer's name, so a screenshot is self-explanatory. */
  title: string;
}

const ZOOM = 16;

/**
 * The buyer's stored coordinates, pinned.
 *
 * Deliberately shows the stored pair rather than re-geocoding the address: the
 * point of the map is to see where Choco thinks this buyer is, including when
 * that is wrong.
 */
export const BuyerLocationMap: React.FC<BuyerLocationMapProps> = ({
  latitude,
  longitude,
  title,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GoogleMapInstance>();
  const markerRef = useRef<{
    setMap(map: GoogleMapInstance | null): void;
  }>();

  useEffect(() => {
    const maps = window.google?.maps;
    if (!maps || !containerRef.current) return undefined;

    const position = { lat: latitude, lng: longitude };

    if (!mapRef.current) {
      mapRef.current = new maps.Map(containerRef.current, {
        center: position,
        zoom: ZOOM,
        streetViewControl: false,
        mapTypeControl: false,
      });
    } else {
      mapRef.current.setCenter(position);
    }

    markerRef.current?.setMap(null);
    markerRef.current = new maps.Marker({
      position,
      map: mapRef.current,
      title,
    });

    return () => {
      markerRef.current?.setMap(null);
      markerRef.current = undefined;
    };
  }, [latitude, longitude, title]);

  return (
    <div
      ref={containerRef}
      data-testid="buyer-location-map"
      className="h-64 w-full overflow-hidden rounded-lg border border-default-200 bg-default-100"
    />
  );
};
